import React, { useState, useEffect } from "react";
import {
  Combobox,
  ComboboxTrigger,
  ComboboxContent,
  ComboboxItems,
  ComboboxInput,
  ComboboxOption,
} from "../Combobox";
import { Divider } from "../Divider";
import { DateRange, QuickFilterItem } from "./FiltersProvider";
import { Button } from "../Button";
import { Link } from "../Link";
import { Box, Flex, Text, useDisclosure, VStack } from "@chakra-ui/react";
import { useTranslation } from "../ManaUIProvider";
import { ModalsContextValue, useModals } from "@saas-ui/react";
import {
  getLocalTimeZone,
  DateRangePicker,
  DateRangePickerTimeField,
  DateRangePickerCalendar,
  DateRangeValue,
} from "@saas-ui/date-picker";
import { parseAbsoluteToLocal } from "@internationalized/date";
import { FilterItem } from "./FiltersProvider";
import {
  DatePickerTranslations,
  FiltersTranslations,
} from "../ManaUIProvider/TranslationProvider";
import { endOfDay, startOfDay } from "date-fns";
import { FALCalendarLines } from "../../icons";
import { formatDateRange } from "../../utils/dateUtils";

interface SelectFilterProps {
  filter: QuickFilterItem;
  selectedOptions: QuickFilterItem[];
  onChange: (options: QuickFilterItem[]) => void;
  onClear: () => void;
  onApply: (options: QuickFilterItem[]) => void;
  onClose: () => void;
  optionToString?: (option: QuickFilterItem) => string;
  filterFunction?: (
    inputValue: string,
    options: QuickFilterItem[],
  ) => QuickFilterItem[];
}

export const SelectFilter = (props: SelectFilterProps) => {
  const translations = useTranslation("Filters");
  const disclosure = useDisclosure();
  const modals = useModals();

  const handleClose = () => {
    disclosure.onClose();
    props?.onClose();
  };

  const isDateRangeFilter =
    props.filter.type === "date-range" ||
    props.filter.type === "date-range-with-time";
  const datePickerTranslations = useTranslation("DatePicker");

  const [actionOptions, setActionOptions] = useState<QuickFilterItem[]>(() => {
    if (typeof props.filter.items === "function") {
      return [];
    }
    return (props.filter.items as FilterItem[]).map((filterItem) =>
      isDateRangeFilter && filterItem.value === "custom"
        ? generateCustomDateRangeOption(
            props.filter,
            filterItem,
            props,
            datePickerTranslations,
            translations,
            modals,
          )
        : (filterItem as QuickFilterItem),
    );
  });

  useEffect(() => {
    if (typeof props.filter.items === "function") {
      const maybeItems = props.filter.items("");
      Promise.resolve(maybeItems)
        .then((items) => {
          const newOptions = items.map((filterItem) =>
            isDateRangeFilter && filterItem.value === "custom"
              ? generateCustomDateRangeOption(
                  props.filter,
                  filterItem,
                  props,
                  datePickerTranslations,
                  translations,
                  modals,
                )
              : (filterItem as QuickFilterItem),
          );
          setActionOptions(newOptions);
        })
        .catch((err) => {
          console.error("Error loading filter items", err);
        });
    }
  }, [
    props.filter.items,
    isDateRangeFilter,
    datePickerTranslations,
    translations,
    modals,
    props,
  ]);

  const getActionOptionsForItemFn = async (
    inputValue: string,
  ): Promise<FilterItem[]> => {
    if (props.filter?.items && typeof props.filter.items === "function") {
      return await props.filter.items(inputValue);
    }
    return new Promise(() => []);
  };

  const [allSelected, setAllSelected] = useState<boolean>(false);
  useEffect(() => {
    if (props && props.selectedOptions && actionOptions) {
      setAllSelected(props.selectedOptions.length === actionOptions.length);
    }
  }, [props, props.selectedOptions, actionOptions]);

  const getTriggerLabel = () => {
    const { selectedOptions, filter, optionToString } = props;
    if (selectedOptions.length === 0) {
      return filter.label;
    }
    if (selectedOptions.length === 1) {
      return optionToString
        ? optionToString(selectedOptions[0])
        : selectedOptions[0].label;
    }
    return filter.label;
  };

  const renderOptionToString = React.useCallback(
    (opt: QuickFilterItem) => {
      if (props.filter.type === "date-range") {
        return (
          <Flex alignItems="center" gap={2}>
            <FALCalendarLines />
            <Text as="span" noOfLines={1}>
              {opt?.label ?? opt?.value?.toString()}
            </Text>
          </Flex>
        );
      }
      if (props.optionToString) {
        return props.optionToString(opt);
      }
      return (opt?.value || "").toString();
    },
    [props],
  );

  const comboboxTriggerLabel = () => {
    if (props.filter.type === "date-range") {
      return (
        <Flex alignItems="center" gap={2}>
          <FALCalendarLines />
          <Text as="span">{getTriggerLabel()}</Text>
        </Flex>
      );
    }
    return getTriggerLabel();
  };

  return (
    <Combobox
      {...disclosure}
      onClose={handleClose}
      multiple={isDateRangeFilter ? false : true}
      isEqual={(a: ComboboxOption<unknown>, b: ComboboxOption<unknown>) => {
        return JSON.stringify(a.value) === JSON.stringify(b.value);
      }}
      // @ts-expect-error combobox types don't support generic options
      selectedOptions={props.selectedOptions}
      // @ts-expect-error combobox types don't support generic options
      setSelectedOptions={props.onChange}
      // @ts-expect-error combobox types don't support generic options
      options={actionOptions || []}
      // @ts-expect-error combobox types don't support generic options
      optionToString={renderOptionToString}
      // @ts-expect-error combobox types don't support generic options
      filterOptions={
        typeof props.filter.items === "function"
          ? async (inputValue: string): Promise<FilterItem[]> => {
              return await getActionOptionsForItemFn(inputValue);
            }
          : (inputValue: string): Promise<FilterItem[]> => {
              if (props.filterFunction) {
                // @ts-expect-error combobox types don't support generic options
                return props.filterFunction(
                  inputValue,
                  actionOptions as QuickFilterItem[],
                );
              }
              // @ts-expect-error combobox types don't support generic options
              return actionOptions.filter((option) => {
                const value = option.value?.toString().toLowerCase() || "";
                const label = option.label?.toString().toLowerCase() || "";
                return (
                  value.includes(inputValue.toLowerCase()) ||
                  label.includes(inputValue.toLowerCase())
                );
              });
            }
      }
    >
      <ComboboxTrigger size="lg">{comboboxTriggerLabel()}</ComboboxTrigger>

      <ComboboxContent>
        {props.filter.showSearchbar && (
          <>
            <ComboboxInput size={"xl"} />
            <Divider my={3.5} palette="neutral.separator" />
          </>
        )}
        {props.filter.selectAll && (
          <Box px={3} py={2.5}>
            <Link
              palette="primary"
              as="button"
              size="md"
              justifyContent="start"
              onClick={() =>
                props.onChange(
                  allSelected ? [] : (actionOptions as FilterItem[]),
                )
              }
            >
              {allSelected ? translations.clearAll : translations.selectAll}
            </Link>
          </Box>
        )}
        <ComboboxItems />
        {!props.filter.applyOnChange && (
          <>
            <Divider my={2.5} palette="neutral.separator" />
            <Flex gap={3} justifyContent={"flex-end"}>
              <Button size="lg" variant="outline.soft" onClick={props.onClear}>
                {translations.clear}
              </Button>
              <Button
                size="lg"
                variant={"primary"}
                onClick={() => {
                  props.onApply(props.selectedOptions);
                  disclosure.onClose();
                }}
              >
                {translations.apply}
              </Button>
            </Flex>
          </>
        )}
      </ComboboxContent>
    </Combobox>
  );
};

function generateCustomDateRangeOption(
  filter: QuickFilterItem,
  filterItem: FilterItem,
  props: SelectFilterProps,
  datePickerTranslations: DatePickerTranslations,
  translations: FiltersTranslations,
  modals: ModalsContextValue,
): QuickFilterItem {
  return {
    ...filterItem,
    onClick: () => {
      let initialDateRange: DateRangeValue | undefined;
      let activeDateRange: DateRangeValue | undefined;
      const hasTimeField = props.filter.type === "date-range-with-time";

      if (props.selectedOptions && props.selectedOptions.length > 0) {
        const currentDates = props.selectedOptions.at(0)?.value as
          | DateRange
          | undefined;
        if (currentDates && currentDates.startDate && currentDates.endDate) {
          initialDateRange = {
            start: parseAbsoluteToLocal(currentDates?.startDate?.toISOString()),
            end: parseAbsoluteToLocal(currentDates?.endDate?.toISOString()),
          };
        }
      }

      const id = modals.open({
        title: filterItem.label,
        contentProps: {
          minW: "fit-content",
        },
        onClose: () => {
          if (initialDateRange) {
            props.onApply([
              amendDateRangeFilterItem(
                filterItem,
                initialDateRange,
                hasTimeField,
              ),
            ]);
          }
        },
        body: (
          <>
            <DateRangePicker
              defaultValue={
                initialDateRange?.start && initialDateRange?.end
                  ? { start: initialDateRange.start, end: initialDateRange.end }
                  : undefined
              }
              minValue={
                filter.minValue &&
                parseAbsoluteToLocal(filter.minValue.toISOString())
              }
              maxValue={
                filter.maxValue &&
                parseAbsoluteToLocal(filter.maxValue.toISOString())
              }
              granularity="minute"
              onChange={(update) => (activeDateRange = update)}
            >
              <DateRangePickerCalendar />
              {hasTimeField && <DateRangePickerTimeField />}
            </DateRangePicker>
          </>
        ),
        footer: (
          <>
            <Button
              onClick={() => {
                if (initialDateRange) {
                  props.onApply([
                    amendDateRangeFilterItem(
                      filterItem,
                      initialDateRange,
                      hasTimeField,
                    ),
                  ]);
                }
                modals.close(id);
              }}
              mr="3"
              variant={"ghost"}
            >
              {datePickerTranslations.cancel}
            </Button>
            <Button
              onClick={() => {
                if (activeDateRange) {
                  props.onApply([
                    amendDateRangeFilterItem(
                      filterItem,
                      activeDateRange,
                      hasTimeField,
                    ),
                  ]);
                  initialDateRange = activeDateRange;
                }
                modals.close(id);
              }}
            >
              {translations.apply}
            </Button>
          </>
        ),
      });
    },
    label: (
      <VStack align="flex-start" spacing={0}>
        <Text>{filterItem.label}</Text>
        {(() => {
          const val = props.selectedOptions[0]?.value;
          if (
            val != null &&
            typeof val === "object" &&
            "startDate" in val &&
            "endDate" in val
          ) {
            const val = props.selectedOptions[0]?.value as
              | DateRange
              | undefined;
            return (
              <Text fontSize="sm" color="text.neutral.strong">
                {val?.startDate && val?.endDate
                  ? formatDateRange(val.startDate, val.endDate, "long")
                  : null}
              </Text>
            );
          }
          return null;
        })()}
      </VStack>
    ),
  };
}

function amendDateRangeFilterItem(
  filterItem: FilterItem,
  dateRange: DateRangeValue,
  hasTimeField: boolean,
): QuickFilterItem {
  const start = dateRange.start?.toDate(getLocalTimeZone());
  const end = dateRange.end?.toDate(getLocalTimeZone());
  if (!start || !end) return filterItem as QuickFilterItem;

  const value: DateRange = hasTimeField
    ? { startDate: start, endDate: end }
    : { startDate: startOfDay(start), endDate: endOfDay(end) };

  const label = `${formatDateRange(value.startDate, value.endDate, "long")}`;

  return {
    ...filterItem,
    value,
    label,
  };
}
